import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './EventCardPage.css';
import { useData } from '../contexts/DataContext';

/**
 * EventCardPage - immersive zoomed view for a single Event, modeled after CourseCardPage.
 * Shows event details + Readings & Assignments.
 */
export default function EventCardPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { getAssignmentsByEvent } = useData();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [assignments, setAssignments] = useState([]);

  const eventIdNum = useMemo(()=> Number(eventId), [eventId]);

  const fetchData = useCallback(async () => {
    if(!eventIdNum) return;
    setLoading(true); setError(null);
    try {
      // We don't have a direct getEvent method in provided hooks; fallback: fetch events for parent? (Assumption)
      // Attempt coarse fetch: getEndeavorEvents requires an endeavor id, which we don't have. So we skip event refresh.
      // In a real setup we'd add getEvent(eventId). For now assignments still load.
      const asnData = await getAssignmentsByEvent(eventIdNum).catch(()=> []);
      const list = Array.isArray(asnData) ? asnData : (asnData?.content || []);
      setAssignments(list);
      // Minimal placeholder event object until dedicated event fetch is available.
      setEvent({ id: eventIdNum, title: `Event ${eventIdNum}`, description: 'Event details coming soon.', location: '', eventTypeName: '' });
    } catch(e) {
      setError(e.message || 'Failed to load event');
    } finally { setLoading(false); }
  }, [eventIdNum, getAssignmentsByEvent]);

  useEffect(()=> { fetchData(); }, [fetchData]);

  const formatDate = (dStr) => { if(!dStr) return ''; try { const d=new Date(dStr); if(isNaN(d)) return ''; return d.toLocaleDateString(undefined,{month:'short', day:'numeric'});} catch { return ''; } };
  const formatTime = (dStr) => { if(!dStr) return ''; try { const d=new Date(dStr); if(isNaN(d)) return ''; return d.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'});} catch { return ''; } };

  if (loading) return <div className="event-card-page-container"><div>Loading…</div></div>;
  if (error) return <div className="event-card-page-container"><div className="ecp-error">{error}</div></div>;
  if (!event) return <div className="event-card-page-container"><div>No event found.</div></div>;

  return (
    <div className="event-card-page-container">
      <div className="event-card-page-header">
        <div className="event-card-page-topline">
          <button className="zoom-back-btn btn btn-outline btn-sm" onClick={()=>navigate(-1)} title="Back"><span className="icon" aria-hidden="true">⤢</span>Back</button>
          <h1 className="event-card-page-title" title={event.title}>{event.title}</h1>
        </div>
        <div className="event-card-page-meta">
          {event.date && <span>{formatDate(event.date)}</span>}
          {event.startTime && <span>{formatTime(event.startTime)}</span>}
          {event.location && <span>{event.location}</span>}
          {event.eventTypeName && <span>{event.eventTypeName}</span>}
        </div>
        {event.description && <div className="event-card-page-sub">{event.description}</div>}
      </div>

      <div className="event-card-page-grid">
        <div className="ecp-panel" style={{gridColumn:'1 / -1'}}>
          <div className="ecp-panel-header"><h2 className="ecp-panel-title"><span className="icon" aria-hidden="true">📚</span> Readings & Assignments</h2></div>
          <div className="ecp-panel-body">
            {assignments.length === 0 && <div className="ecp-empty">No assignments</div>}
            {assignments.map(a => {
              const id = a.assignmentId || a.id;
              const title = a.title || a.name || 'Assignment';
              return (
                <div key={id} className="ecp-item" onClick={()=>navigate(`/assignment/${id}`)} title={title}>
                  <div className="ecp-item-title">{title}</div>
                  {a.description && <div className="ecp-item-desc">{(a.description || '').slice(0,220)}</div>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="ecp-panel">
          <div className="ecp-panel-header"><h2 className="ecp-panel-title"><span className="icon" aria-hidden="true">🧭</span> Event Overview</h2></div>
          <div className="ecp-panel-body">
            <div className="ecp-item-static">Additional event details will appear here when event fetch API is wired.</div>
          </div>
        </div>

      </div>
    </div>
  );
}
